import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useUnit } from "effector-react";
import { $notificationsStore } from "@/features/notifications/store";
import "./NotificationsModal.scss";
import { imageList } from "@/core/utils/imageList";
import { SvgIcon } from "@/core/components/svgIcon/SvgIcon";
import { Link } from "react-router-dom";
import { notificationsService } from "@/features/notifications";
import { fetchNotificationsRequested } from "@/features/notifications/store";

interface NotificationsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationsModal: React.FC<NotificationsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [notificationsStore] = useUnit([$notificationsStore]);
  const [activeTab, setActiveTab] = useState<"all" | "unread">("unread");

  const formatTimeAgo = useCallback((timestamp: string) => {
    // Parse DD-MM-YYYY HH:mm:ss format
    const [datePart, timePart] = timestamp.split(" ");
    const [day, month, year] = datePart.split("-");
    const [hours, minutes, seconds] = timePart.split(":");

    // Create date in YYYY-MM-DD HH:mm:ss format (ISO-like)
    const date = new Date(
      `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`,
    );

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return "Invalid date";
    }

    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 0) return "Just now"; // Future date
    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600)
      return `${Math.floor(diffInSeconds / 60)} min ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800)
      return `${Math.floor(diffInSeconds / 86400)} days ago`;
    return `${Math.floor(diffInSeconds / 604800)} weeks ago`;
  }, []);

  const filteredNotifications = useMemo(() => {
    return activeTab === "unread"
      ? notificationsStore.notifications.filter(
          (notification) => notification.is_read === "0",
        )
      : notificationsStore.notifications;
  }, [activeTab, notificationsStore.notifications]);

  const unreadClickHandler = async (id: string) => {
    await notificationsService.markAsRead(id);
    fetchNotificationsRequested();
  };

  const handleTabChange = useCallback((tab: "all" | "unread") => {
    setActiveTab(tab);
  }, []);

  const handleImageError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      (e.target as HTMLImageElement).src = "/default-avatar.png";
    },
    [],
  );

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <div className="notifications-overlay" onClick={onClose} />
      <div className="notifications-modal">
        <div className="notifications-header">
          <h3>Notifications</h3>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="notifications-tabs">
          <button
            className={`tab ${activeTab === "unread" ? "active" : ""}`}
            onClick={() => handleTabChange("unread")}
          >
            Unread
          </button>
          <button
            className={`tab ${activeTab === "all" ? "active" : ""}`}
            onClick={() => handleTabChange("all")}
          >
            All
          </button>

          <div className="tabs-actions">
            {/* <button className="dropdown-icon">⌄</button> */}
            <Link to={"/settings/notifications"} className="settings-icon">
              <SvgIcon icon="icon-settings2" />
            </Link>
          </div>
        </div>

        <div className="notifications-list">
          {filteredNotifications.map((notification) => (
            <div
              key={notification.id}
              className={`notification-item ${
                !notification.is_read ? "unread" : ""
              }`}
              onClick={() => {
                if (notification.is_read === "0") {
                  //console.log(notification.is_read, "is_read");
                  unreadClickHandler(notification.id);
                }
              }}
            >
              {/* <div className="notification-avatar">
                <img src={imageList.noimage} alt="User Avatar" />
              </div> */}
              <div className="notification-content">
                <div className="notification-header">
                  {/* <span className="sender-name">EuCalls</span> */}
                  <span className="timestamp">
                    {formatTimeAgo(notification.created)}
                  </span>
                  {!notification.is_read && <div className="unread-dot" />}
                </div>
                <div
                  className="notification-message"
                  dangerouslySetInnerHTML={{ __html: notification.label }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default NotificationsModal;
